<template>
    <UPopover :popper="{ placement: 'bottom-start' }">
        <UButton color="white" size="lg" class="w-full justify-between" trailing-icon="i-ph-calendar-duotone" :ui="{ icon: { base: 'opacity-40' } }">
            <template v-if="date">
                <span v-if="isSameDay(date.start as Date, date.end as Date)" class="font-medium">{{ format(date.start as Date, 'MMM d, yyy') }}</span>
                <span v-else class="font-medium">{{ format(date.start as Date, 'MMM d, yyy') }} - {{ format(date.end as Date, 'MMM d, yyy') }}</span>
            </template>
            <span v-else class="text-gray-400 font-normal">Select...</span>
        </UButton>

        <template #panel="{ close }">
            <div class="flex items-center sm:divide-x divide-gray-200 dark:divide-gray-800">
                <div class="flex flex-col">
                    <UButton
                        v-for="(range, index) in ranges"
                        :key="index"
                        :label="range.label"
                        color="gray"
                        variant="ghost"
                        class="rounded-none px-6"
                        :class="[
                            isRangeSelected(range.duration) ? 'bg-gray-100 dark:bg-gray-800' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50',
                        ]"
                        truncate
                        @click="selectRange(range.duration)"
                    />

                    <UButton
                        label="Clear"
                        color="gray"
                        @click="clear(close)"
                        class="block mt-4 mx-6 text-center"
                    />
                </div>

                <VCalendarDatePicker
                    v-model.range="date"
                    v-bind="{ ...attrs, ...$attrs }"
                    @update:model-value="close"
                    :columns="2" 
                />
            </div>
        </template>
    </UPopover>
</template>

<script setup lang="ts">
import { sub, format, isSameDay, type Duration } from 'date-fns'
import { DatePicker as VCalendarDatePicker } from 'v-calendar'
import type { DatePickerRangeObject } from 'v-calendar/dist/types/src/use/datePicker.js'
import 'v-calendar/dist/style.css'

const attrs = {
    transparent: true,
    borderless: true,
    color: 'primary',
    'is-dark': { selector: 'html', darkClass: 'dark' },
    'first-day-of-week': 2,
}

const ranges = [
    { label: 'Last 7 days', duration: { days: 7 } },
    { label: 'Last 14 days', duration: { days: 14 } },
    { label: 'Last 30 days', duration: { days: 30 } },
    { label: 'Last 3 months', duration: { months: 3 } },
    { label: 'Last 6 months', duration: { months: 6 } },
    { label: 'Last year', duration: { years: 1 } },
]

const emit = defineEmits(['update:model-value'])

const props = defineProps({
    modelValue: {
        type: {} as PropType<DatePickerRangeObject | null>,
        default: null
    }
})

const date = computed({
    get: () => props.modelValue,
    set: (value) => {
        emit('update:model-value', value)
    }
})

function isRangeSelected(duration: Duration) {
    return date.value && date.value.start && date.value.end && isSameDay(date.value.start as Date, sub(new Date(), duration)) && isSameDay(date.value.end as Date, new Date())
}

function selectRange(duration: Duration) {
    date.value = { start: sub(new Date(), duration), end: new Date() }
}

function clear(close: Function) {
    date.value = null
    close()
}
</script>

<style>
:root {
  --vc-gray-50: rgb(var(--color-gray-50));
  --vc-gray-100: rgb(var(--color-gray-100));
  --vc-gray-200: rgb(var(--color-gray-200));
  --vc-gray-300: rgb(var(--color-gray-300));
  --vc-gray-400: rgb(var(--color-gray-400));
  --vc-gray-500: rgb(var(--color-gray-500));
  --vc-gray-600: rgb(var(--color-gray-600));
  --vc-gray-700: rgb(var(--color-gray-700));
  --vc-gray-800: rgb(var(--color-gray-800));
  --vc-gray-900: rgb(var(--color-gray-900));
}

.vc-primary {
  --vc-accent-50: rgb(var(--color-primary-50));
  --vc-accent-100: rgb(var(--color-primary-100));
  --vc-accent-200: rgb(var(--color-primary-200));
  --vc-accent-300: rgb(var(--color-primary-300));
  --vc-accent-400: rgb(var(--color-primary-400));
  --vc-accent-500: rgb(var(--color-primary-500));
  --vc-accent-600: rgb(var(--color-primary-600));
  --vc-accent-700: rgb(var(--color-primary-700));
  --vc-accent-800: rgb(var(--color-primary-800));
  --vc-accent-900: rgb(var(--color-primary-900));
}
</style>
